/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _spliceInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/splice";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _forEachInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/for-each";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";

/** @module core/classes/log */
import { Types } from '@codistica/types';
import { noop } from '../modules/noop.js';
import { getLength } from '../modules/object-utils/internals/get-length.js';
import { isObject } from '../modules/object-utils/internals/is-object.js';
import { isPrimitive } from '../modules/object-utils/internals/is-primitive.js';
import { prettify } from '../modules/object-utils/internals/prettify.js';
import { injectBefore } from '../modules/string-utils/internals/inject-before.js';
import { stringifyPrimitive } from '../modules/stringify-primitive.js';
var logSchema = new Types({
  options: {
    type: 'Object',
    def: {
      logLvl: {
        type: 'number',
        def: 9
      },
      typeSize: {
        type: 'number',
        def: 15
      },
      callerSize: {
        type: 'number',
        def: 25
      },
      loggers: {
        type: 'Array',
        def: []
      },
      consoleBinder: {
        type: ['Function', 'null'],
        def: null
      }
    }
  }
});
/**
 * @typedef logLogObjType
 * @property {string} type - Log type.
 * @property {string} color - Log color.
 * @property {string} backgroundColor - Log background color.
 * @property {string} caller - Log caller.
 * @property {*} msg - Log message.
 * @property {string} consoleMethod - Console method to be used.
 * @property {Date} date - Log date.
 */

/**
 * @callback logConsoleBinderSendLogType
 * @param {logLogObjType} logObj - The log object.
 * @returns {Function} Bound log function.
 */

/**
 * @callback logLoggerSendLogType
 * @param {logLogObjType} logObj - The log object.
 * @returns {void} Void.
 */

/**
 * @typedef logConsoleBinderType
 * @property {logConsoleBinderSendLogType} sendLog - Send log method.
 */

/**
 * @typedef logLoggerType
 * @property {logLoggerSendLogType} sendLog - Send log method.
 */

/**
 * @typedef logOptionsType
 * @property {number} [logLvl=9] - Log level.
 * @property {number} [typeSize=15] - Log type string size.
 * @property {number} [callerSize=25] - Log caller string size.
 * @property {Array<logLoggerType>} [loggers=[]] - Loggers array.
 * @property {(logConsoleBinderType|null)} [consoleBinder=null] - Console binder.
 */

/**
 * @classdesc A class for logging management.
 */

var Log = /*#__PURE__*/function () {
  /**
   * @description Constructor.
   * @param {logOptionsType} [options] - Log options object.
   */
  function Log(options) {
    _classCallCheck(this, Log);

    var _logSchema$validate = logSchema.validate({
      options: options
    });

    options = _logSchema$validate.options;

    /** @type {logOptionsType} */
    this.options = options;
  }
  /**
   * @instance
   * @description Send fatal message.
   * @param {string} caller - Log caller.
   * @param {*} msg - Log message.
   * @returns {Function} Bound log function.
   */


  _createClass(Log, [{
    key: "fatal",
    value: function fatal(caller, msg) {
      if (this.options.logLvl >= 1 && this.canLog(msg)) {
        return this.dispatchLog({
          type: '[FATAL]',
          color: '#ffffff',
          backgroundColor: '#ff0000',
          caller: caller,
          msg: msg,
          consoleMethod: 'error',
          date: new Date()
        });
      } else {
        return noop;
      }
    }
    /**
     * @instance
     * @description Send error message.
     * @param {string} caller - Log caller.
     * @param {*} msg - Log message.
     * @returns {Function} Bound log function.
     */

  }, {
    key: "error",
    value: function error(caller, msg) {
      if (this.options.logLvl >= 2 && this.canLog(msg)) {
        return this.dispatchLog({
          type: '[ERROR]',
          color: '#990000',
          backgroundColor: 'transparent',
          caller: caller,
          msg: msg,
          consoleMethod: 'error',
          date: new Date()
        });
      } else {
        return noop;
      }
    }
    /**
     * @instance
     * @description Send warning message.
     * @param {string} caller - Log caller.
     * @param {*} msg - Log message.
     * @returns {Function} Bound log function.
     */

  }, {
    key: "warning",
    value: function warning(caller, msg) {
      if (this.options.logLvl >= 3 && this.canLog(msg)) {
        return this.dispatchLog({
          type: '[WARNING]',
          color: '#cc9900',
          backgroundColor: 'transparent',
          caller: caller,
          msg: msg,
          consoleMethod: 'warn',
          date: new Date()
        });
      } else {
        return noop;
      }
    }
    /**
     * @instance
     * @description Send info message.
     * @param {string} caller - Log caller.
     * @param {*} msg - Log message.
     * @returns {Function} Bound log function.
     */

  }, {
    key: "info",
    value: function info(caller, msg) {
      if (this.options.logLvl >= 4 && this.canLog(msg)) {
        return this.dispatchLog({
          type: '[INFO]',
          color: '#000099',
          backgroundColor: 'transparent',
          caller: caller,
          msg: msg,
          consoleMethod: 'info',
          date: new Date()
        });
      } else {
        return noop;
      }
    }
    /**
     * @instance
     * @description Send success message.
     * @param {string} caller - Log caller.
     * @param {*} msg - Log message.
     * @returns {Function} Bound log function.
     */

  }, {
    key: "success",
    value: function success(caller, msg) {
      if (this.options.logLvl >= 5 && this.canLog(msg)) {
        return this.dispatchLog({
          type: '[SUCCESS]',
          color: '#009900',
          backgroundColor: 'transparent',
          caller: caller,
          msg: msg,
          consoleMethod: 'log',
          date: new Date()
        });
      } else {
        return noop;
      }
    }
    /**
     * @instance
     * @description Send result message.
     * @param {string} caller - Log caller.
     * @param {*} msg - Log message.
     * @returns {Function} Bound log function.
     */

  }, {
    key: "result",
    value: function result(caller, msg) {
      if (this.options.logLvl >= 6 && this.canLog(msg)) {
        return this.dispatchLog({
          type: '[RESULT]',
          color: '#99ccff',
          backgroundColor: 'transparent',
          caller: caller,
          msg: msg,
          consoleMethod: 'log',
          date: new Date()
        });
      } else {
        return noop;
      }
    }
    /**
     * @instance
     * @description Send progress message.
     * @param {string} caller - Log caller.
     * @param {*} msg - Log message.
     * @returns {Function} Bound log function.
     */

  }, {
    key: "progress",
    value: function progress(caller, msg) {
      if (this.options.logLvl >= 7 && this.canLog(msg)) {
        return this.dispatchLog({
          type: '[PROGRESS]',
          color: '#8a2be2',
          backgroundColor: 'transparent',
          caller: caller,
          msg: msg,
          consoleMethod: 'log',
          date: new Date()
        });
      } else {
        return noop;
      }
    }
    /**
     * @instance
     * @description Send verbose message.
     * @param {string} caller - Log caller.
     * @param {*} msg - Log message.
     * @returns {Function} Bound log function.
     */

  }, {
    key: "verbose",
    value: function verbose(caller, msg) {
      if (this.options.logLvl >= 8 && this.canLog(msg)) {
        return this.dispatchLog({
          type: '[VERBOSE]',
          color: '#999999',
          backgroundColor: 'transparent',
          caller: caller,
          msg: msg,
          consoleMethod: 'log',
          date: new Date()
        });
      } else {
        return noop;
      }
    }
    /**
     * @instance
     * @description Send debug message.
     * @param {string} caller - Log caller.
     * @param {*} msg - Log message.
     * @returns {Function} Bound log function.
     */

  }, {
    key: "debug",
    value: function debug(caller, msg) {
      if (this.options.logLvl >= 9 && this.canLog(msg)) {
        return this.dispatchLog({
          type: '[DEBUG]',
          color: '#ff66ff',
          backgroundColor: 'transparent',
          caller: caller,
          msg: msg,
          consoleMethod: 'debug',
          date: new Date()
        });
      } else {
        return noop;
      }
    }
    /**
     * @instance
     * @description Prepare and send the log object.
     * @param {logLogObjType} logObj - The log object.
     * @returns {Function} Bound log function.
     */

  }, {
    key: "dispatchLog",
    value: function dispatchLog(logObj) {
      var _context,
          _this = this,
          _context2;

      // FORWARD ERROR MESSAGES
      // TODO: IMPROVE. CONSIDER Catcher CLASS
      if (logObj.msg instanceof Error) {
        return this.error(logObj.caller, {
          name: logObj.msg.name,
          message: logObj.msg.message,
          stack: logObj.msg.stack || ''
        });
      } // COMPOSE LOG


      this.composeLog(logObj); // CALL LOGGERS

      _forEachInstanceProperty(_context = this.options.loggers).call(_context, _bindInstanceProperty(_context2 = function _context2(logger) {
        _newArrowCheck(this, _this);

        logger.sendLog(logObj);
      }).call(_context2, this)); // CALL CONSOLE BINDER AND RETURN BOUND LOG FUNCTION


      if (this.options.consoleBinder !== null) {
        return this.options.consoleBinder.sendLog(logObj);
      } else {
        return noop;
      }
    }
    /**
     * @instance
     * @description Adjust log segments according to options.
     * @param {logLogObjType} logObj - The log object.
     * @returns {void} Void.
     */

  }, {
    key: "composeLog",
    value: function composeLog(logObj) {
      if (isPrimitive(logObj.msg)) {
        logObj.msg = injectBefore(stringifyPrimitive(logObj.msg), this.options.typeSize - logObj.type.length, ' ');
      } else {
        logObj.msg = '\n' + prettify(logObj.msg);
      }

      logObj.caller = injectBefore(logObj.caller, this.options.callerSize - logObj.caller.length, ' ');
    }
    /**
     * @instance
     * @description Adds a logger to the loggers array and returns respective index.
     * @param {logLoggerType} logger - The logger instance to be added.
     * @returns {(number|null)} Added logger index.
     */

  }, {
    key: "addLogger",
    value: function addLogger(logger) {
      if (this.getLoggerIndex(logger.constructor.name) !== null) {
        return null;
      } else {
        return this.options.loggers.push(logger) - 1;
      }
    }
    /**
     * @instance
     * @description Removes the specified logger.
     * @param {string} loggerName - Name of logger to be removed.
     * @returns {boolean} True if a logger has been removed and false otherwise.
     */

  }, {
    key: "removeLogger",
    value: function removeLogger(loggerName) {
      var loggerIndex = this.getLoggerIndex(loggerName);

      if (loggerIndex !== null) {
        var _context3;

        _spliceInstanceProperty(_context3 = this.options.loggers).call(_context3, loggerIndex, 1);

        return true;
      } else {
        return false;
      }
    }
    /**
     * @instance
     * @description Removes all loggers.
     * @returns {void} Void.
     */

  }, {
    key: "clearLoggers",
    value: function clearLoggers() {
      this.options.loggers = [];
    }
    /**
     * @instance
     * @description Searches and returns specified logger index if found.
     * @param {string} loggerName - Logger name.
     * @returns {(number|null)} Found logger index or null.
     */

  }, {
    key: "getLoggerIndex",
    value: function getLoggerIndex(loggerName) {
      var length = this.options.loggers.length;

      for (var i = 0; i < length; i++) {
        if (this.options.loggers[i].constructor.name === loggerName) {
          return i;
        }
      }

      return null;
    }
    /**
     * @instance
     * @description Setups the passed console binder.
     * @param {logConsoleBinderType} consoleBinder - The console binder.
     * @param {boolean} [force] - Replace console binder if already exists.
     * @returns {void} Void.
     */

  }, {
    key: "setConsoleBinder",
    value: function setConsoleBinder(consoleBinder, force) {
      if (this.options.consoleBinder === null || force) {
        this.options.consoleBinder = consoleBinder;
      } else {
        this.warning('Log()', 'THERE IS ALREADY A consoleBinder. USE force TO REPLACE. ABORTING')();
      }
    }
    /**
     * @instance
     * @description Remove the current console binder.
     * @returns {boolean} True if a console binder has been removed and false otherwise.
     */

  }, {
    key: "removeConsoleBinder",
    value: function removeConsoleBinder() {
      if (this.options.consoleBinder !== null) {
        this.options.consoleBinder = null;
        return true;
      } else {
        return false;
      }
    }
    /**
     * @instance
     * @description Indicates if logger can dispatch log with current state.
     * @param {*} msg - Input value.
     * @returns {boolean} Result.
     */

  }, {
    key: "canLog",
    value: function canLog(msg) {
      return !!(Log.isValidMsg(msg) && (this.options.consoleBinder || this.options.loggers.length));
    }
    /**
     * @description Returns true if the passed value is considered a valid message and false otherwise.
     * @param {*} msg - Input value.
     * @returns {boolean} Result.
     */

  }], [{
    key: "isValidMsg",
    value: function isValidMsg(msg) {
      return typeof msg !== 'undefined' && msg !== '' && (!isObject(msg) || getLength(msg) !== 0);
    }
  }]);

  return Log;
}();

export { Log };